import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";

const DEFAULT_ROWS = 14;
const DEFAULT_COLS = 22;

const TOOL_DEF = {
  select: { color: "#e5e7eb", text: "#111827" },
  rack: { color: "#475569", text: "#ffffff" },
  bin: { color: "#facc15", text: "#111827" },
  stairs: { color: "#f59e0b", text: "#111827" },
  staging: { color: "#fdba74", text: "#111827" },
  packStation: { color: "#1e3a8a", text: "#ffffff" },
  door: { color: "#334155", text: "#ffffff" },
  receiving: { color: "#4ade80", text: "#14532d" },
  shipping: { color: "#22d3ee", text: "#083344" },
  desk: { color: "#6366f1", text: "#ffffff" },
};

const defaultBounds = () => ({
  minRow: 0,
  maxRow: DEFAULT_ROWS - 1,
  minCol: 0,
  maxCol: DEFAULT_COLS - 1,
});

const normalizeLevel = (level, index = 0) => ({
  id: level?.id || `L${index + 1}`,
  name: level?.name || `Level ${index + 1}`,
  bounds: {
    ...defaultBounds(),
    ...(level?.bounds || {}),
  },
  cells: level?.cells || {},
});

const normalizeLayout = (layout) => {
  if (!layout || !Array.isArray(layout?.levels) || !layout.levels.length) {
    return { levels: [], activeLevelId: "" };
  }
  const levels = layout.levels.map((level, index) => normalizeLevel(level, index));
  return {
    levels,
    activeLevelId: layout?.activeLevelId || levels[0]?.id || "",
  };
};

const buildGridBounds = (level) => {
  const bounds = level?.bounds || defaultBounds();
  const rows = [];
  for (let r = bounds.minRow; r <= bounds.maxRow; r += 1) rows.push(r);
  const cols = [];
  for (let c = bounds.minCol; c <= bounds.maxCol; c += 1) cols.push(c);
  return { rows, cols };
};

const getCellKey = (row, col) => `${row}:${col}`;

const WarehouseGridCell = memo(function WarehouseGridCell({
  cell,
  selected,
  onSelect,
  disabled,
  showOnlyBinsSelectable,
}) {
  const isBin = cell?.type === "bin";
  const isSelectable = !disabled && (!showOnlyBinsSelectable || isBin);
  const cfg = cell ? (TOOL_DEF[cell.type] || TOOL_DEF.select) : TOOL_DEF.select;
  const label = cell?.label || "";

  return (
    <Box
      component="button"
      type="button"
      onClick={isSelectable ? onSelect : undefined}
      disabled={!isSelectable}
      aria-pressed={selected}
      sx={{
        appearance: "none",
        minWidth: 0,
        width: "100%",
        aspectRatio: "1 / 1",
        p: 0,
        borderRadius: 0.4,
        border: selected
          ? "2px solid #ef4444"
          : isBin
            ? "1px solid #d1a900"
            : "1px solid #dbe3ee",
        backgroundColor: cell ? cfg.color : "#f8fafc",
        color: cell ? cfg.text : "#94a3b8",
        opacity: cell ? 1 : 0.55,
        cursor: isSelectable ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        lineHeight: 1,
        transition: "transform 80ms ease, border-color 80ms ease",
        "&:hover": isSelectable
          ? {
              transform: "translateY(-1px)",
              borderColor: "#dc2626",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          px: 0.25,
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textAlign: "center",
          fontWeight: isBin ? 700 : 500,
        }}
      >
        {isBin ? (label || "BIN").slice(0, 7) : ""}
      </Box>
    </Box>
  );
});

export default function WarehouseBinPickerMap({
  layout,
  activeWarehouseKey,
  selectedBinKey,
  onSelectBin,
  disabled = false,
  showOnlyBinsSelectable = true,
}) {
  const normalizedLayout = useMemo(() => normalizeLayout(layout), [layout]);
  const [activeLevelId, setActiveLevelId] = useState(normalizedLayout.activeLevelId);

  useEffect(() => {
    setActiveLevelId(normalizedLayout.activeLevelId);
  }, [normalizedLayout.activeLevelId, activeWarehouseKey]);

  const levels = normalizedLayout.levels;
  const activeLevel = useMemo(
    () => levels.find((level) => level.id === activeLevelId) || levels[0] || null,
    [levels, activeLevelId]
  );
  const grid = useMemo(() => buildGridBounds(activeLevel), [activeLevel]);

  const selectedSummary = useMemo(() => {
    if (!selectedBinKey || !activeLevel) return "";
    const [levelId, cellKey] = String(selectedBinKey).split(":");
    const level = levels.find((entry) => entry.id === levelId) || activeLevel;
    const cell = level?.cells?.[cellKey];
    if (!cell) return "";
    return `${level?.name || level?.id || levelId} - ${cell?.label || cellKey}`;
  }, [selectedBinKey, levels, activeLevel]);

  const renderedCells = useMemo(
    () =>
      grid.rows.flatMap((row) =>
        grid.cols.map((col) => {
          const key = getCellKey(row, col);
          const cell = activeLevel?.cells?.[key];
          const composedKey = activeLevel ? `${activeLevel.id}:${key}` : key;
          return (
            <WarehouseGridCell
              key={composedKey}
              cell={cell}
              selected={selectedBinKey === composedKey}
              disabled={disabled}
              showOnlyBinsSelectable={showOnlyBinsSelectable}
              onSelect={() => {
                if (!activeLevel || cell?.type !== "bin") return;
                onSelectBin?.({
                  key: composedKey,
                  warehouseLevel: activeLevel.id,
                  warehouseCell: key,
                  label: cell?.label || `Bin ${key}`,
                  levelName: activeLevel?.name || activeLevel?.id,
                  cell,
                });
              }}
            />
          );
        })
      ),
    [grid.rows, grid.cols, activeLevel, selectedBinKey, disabled, showOnlyBinsSelectable, onSelectBin]
  );

  if (!activeLevel) {
    return (
      <Typography variant="body2" color="text.secondary">
        No warehouse layout found for this warehouse yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
        <FormControl size="small" sx={{ maxWidth: 260 }}>
          <InputLabel id="warehouse-bin-picker-level-select">Level</InputLabel>
          <Select
            labelId="warehouse-bin-picker-level-select"
            label="Level"
            value={activeLevelId || activeLevel.id}
            onChange={(event) => setActiveLevelId(event.target.value)}
            disabled={disabled}
          >
            {levels.map((level) => (
              <MenuItem key={level.id} value={level.id}>
                {level.name || level.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" sx={{ opacity: 0.72 }}>
          {selectedSummary || "Click any bin to select a warehouse location."}
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, overflow: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${grid.cols.length || 1}, minmax(28px, 1fr))`,
            gap: 0.4,
            minWidth: Math.max(900, (grid.cols.length || 1) * 30),
            background: "linear-gradient(#f8fafc,#eef2f7)",
            p: 1,
            borderRadius: 1,
            border: "1px solid #dbe3ee",
          }}
        >
          {renderedCells}
        </Box>
      </Paper>
    </Stack>
  );
}
